import React, { useState } from 'react';
import { Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const SUPPORT_TYPES = [
  'Electrical Installation Support',
  'Mechanical Installation Support',
  'Automation / PLC Support',
  'Commissioning & Start-up Support',
  'Site Supervision',
  'Project Coordination',
  'Technical Personnel',
  'Troubleshooting & Technical Support',
  'Other',
];

const INITIAL_FORM = {
  name: '',
  company: '',
  email: '',
  phone: '',
  projectLocation: '',
  supportType: '',
  expectedStartDate: '',
  projectDetails: '',
};

export const Contact = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, supportType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.company ||
      !formData.email ||
      !formData.supportType ||
      !formData.projectDetails
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value || '');
      });
      // Honeypot spam field — populated only by bots, silently rejected server-side
      submitData.append('website', document.getElementById('_hp_website')?.value || '');

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          'Thank you. Your project inquiry has been received. Our team will contact you shortly.'
        );
        setFormData(INITIAL_FORM);
      } else {
        toast.error(result.detail || 'Failed to send inquiry. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full border-gray-300 focus:border-blue-600 focus:ring-blue-600';
  const labelCls = 'text-gray-700 font-medium mb-2 block text-sm';

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-8"></div>
            <h3
              data-testid="contact-heading"
              className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-5 max-w-3xl leading-snug"
            >
              Need engineering or technical support for a project in China?
            </h3>
            <p className="text-base md:text-lg text-gray-700 max-w-3xl leading-relaxed">
              Tell us about your project, location, required technical support and expected schedule. Our team will review your requirements and discuss how Tower Technology can support your project in China.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-7 md:p-8 shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                      <Mail className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Email</div>
                      <a
                        href="mailto:office@towertech-eng.com"
                        className="text-gray-700 hover:text-blue-700 transition-colors break-all"
                      >
                        office@towertech-eng.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                      <MapPin className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Location</div>
                      <div className="text-gray-700">Xiamen (Fujian), China</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3 bg-white rounded-2xl p-7 md:p-8 shadow-md border border-gray-100">
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="contact-form"
                noValidate
              >
                {/* Honeypot field — hidden from users, filled only by spam bots */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-10000px',
                    top: 'auto',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                  }}
                >
                  <label htmlFor="_hp_website">Website (leave blank)</label>
                  <input
                    id="_hp_website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className={labelCls}>
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      autoComplete="name"
                      className={inputCls}
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className={labelCls}>
                      Company <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      autoComplete="organization"
                      className={inputCls}
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className={labelCls}>
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      autoComplete="email"
                      className={inputCls}
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className={labelCls}>
                      Phone / WhatsApp
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="tel"
                      className={inputCls}
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="projectLocation" className={labelCls}>
                      Project Location in China
                    </Label>
                    <Input
                      id="projectLocation"
                      name="projectLocation"
                      type="text"
                      placeholder="e.g. Shanghai, Guangdong, Suzhou"
                      value={formData.projectLocation}
                      onChange={handleInputChange}
                      className={inputCls}
                      data-testid="contact-location"
                    />
                  </div>

                  <div>
                    <Label htmlFor="expectedStartDate" className={labelCls}>
                      Expected Project Start Date
                    </Label>
                    <Input
                      id="expectedStartDate"
                      name="expectedStartDate"
                      type="date"
                      value={formData.expectedStartDate}
                      onChange={handleInputChange}
                      className={inputCls}
                      data-testid="contact-start-date"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="supportType" className={labelCls}>
                    Type of Support Required <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.supportType}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger
                      id="supportType"
                      className="w-full border-gray-300 focus:ring-blue-600"
                      data-testid="contact-support-type"
                    >
                      <SelectValue placeholder="Select the type of support" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORT_TYPES.map((type) => (
                        <SelectItem
                          key={type}
                          value={type}
                          data-testid={`support-option-${type
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="projectDetails" className={labelCls}>
                    Project Details <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="projectDetails"
                    name="projectDetails"
                    placeholder="Briefly describe the project, equipment or production line, required support, project location and expected duration."
                    value={formData.projectDetails}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className={`${inputCls} resize-none`}
                    data-testid="contact-project-details"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-base font-semibold rounded-lg shadow-md hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Project Inquiry
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500">
                  <span className="text-red-500">*</span> Required fields
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
